import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Github,
  Globe,
  AtSign,
  BookOpen,
  MessageCircle,
  Music2,
  MoreHorizontal,
  Sparkles,
  Shield,
  Target,
  Link2,
  PenLine,
  Activity,
} from "lucide-react";
import { BlueskyIcon } from "@/components/BlueskyIcon";
import { XIcon } from "@/components/XIcon";
import { LimitHitCard, ResultCard, bandFor } from "@/components/tools/ResultCard";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";

export const Route = createFileRoute("/tools/social-profile-scan")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://scottcbusiness.com/tools/social-profile-scan" },
      { name: "twitter:title", content: "Social Profile Trust Scan | Scott Cunningham" },
      { name: "twitter:description", content: "Check whether your social bios on LinkedIn, X, Bluesky, and more build trust or just list what you do. Free 1-minute diagnostic." },

      { title: "Social Profile Trust Scan | Scott Cunningham" },
      {
        name: "description",
        content:
          "Check whether your social bios on LinkedIn, X, Bluesky, and more build trust or just list what you do. Free 1-minute diagnostic.",
      },
      { property: "og:title", content: "Social Profile Trust Scan" },
      {
        property: "og:description",
        content:
          "Paste your social profile bio and get a quick trust signal diagnostic. No rewrite, just the gaps that matter.",
      },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/tools/social-profile-scan" }],
  }),
  component: SocialProfileScanPage,
});

const STORAGE_KEY = "tool:social-profile-scan:done";

type PlatformDef = {
  name: string;
  limit: number | null;
  field: string;
  Icon: React.ComponentType<{ className?: string }>;
};
const PLATFORM_DEFS: PlatformDef[] = [
  { name: "LinkedIn (headline)", limit: 220, field: "headline", Icon: Linkedin },
  { name: "LinkedIn (about)", limit: 2600, field: "About section", Icon: Linkedin },
  { name: "X", limit: 160, field: "bio", Icon: XIcon },
  { name: "Instagram", limit: 150, field: "bio", Icon: Instagram },
  { name: "Threads", limit: 150, field: "bio", Icon: AtSign },
  {
    name: "Bluesky",
    limit: 256,
    field: "bio",
    Icon: ({ className }) => <BlueskyIcon className={className} />,
  },
  { name: "Facebook (intro)", limit: 101, field: "intro", Icon: Facebook },
  { name: "TikTok", limit: 80, field: "bio", Icon: Music2 },
  { name: "YouTube (about)", limit: 1000, field: "About section", Icon: Youtube },
  { name: "GitHub", limit: 160, field: "bio", Icon: Github },
  { name: "Mastodon", limit: 500, field: "bio", Icon: MessageCircle },
  { name: "Substack / Medium", limit: 300, field: "bio", Icon: BookOpen },
  { name: "Personal website bio", limit: null, field: "bio", Icon: Globe },
  { name: "Other", limit: null, field: "bio", Icon: MoreHorizontal },
];
const PLATFORM_LIMITS: Record<string, { limit: number | null; field: string }> = Object.fromEntries(
  PLATFORM_DEFS.map((p) => [p.name, { limit: p.limit, field: p.field }]),
);
const PLATFORMS = PLATFORM_DEFS.map((p) => p.name);
const GOALS = [
  "Get hired",
  "Win clients",
  "Build authority",
  "Grow an audience",
  "Promote a company",
  "Clarify my positioning",
  "Other",
];

const LEVEL_COPY: Record<
  "needs-work" | "developing" | "strong" | "excellent",
  {
    label: string;
    diagnosis: string;
    nextStep: string;
    cta: { headline: string; body: string; primaryLabel: string; secondaryLabel: string };
  }
> = {
  "needs-work": {
    label: "Trust signals: needs work",
    diagnosis:
      "Your profile may be creating confusion or leaving trust on the table. It needs clearer positioning, stronger proof, and a more obvious reason for the right person to connect, hire, follow, or contact you.",
    nextStep:
      "Lead with a sharper identity, add one concrete proof point, and end with a clear next step.",
    cta: {
      headline: "Turn your profile into a clearer trust signal.",
      body: "Scott can help tighten your positioning, sharpen your headline or bio, add stronger proof points, and make your profile easier for the right audience to understand.",
      primaryLabel: "Improve My Social Presence",
      secondaryLabel: "View Services",
    },
  },
  developing: {
    label: "Trust signals: developing",
    diagnosis:
      "Your profile has a usable foundation, but it may not be specific or credible enough yet. The next step is making your value clearer, your proof stronger, and your next step easier to act on.",
    nextStep:
      "Get more specific about who you help and how, and add a credibility marker your audience will recognize.",
    cta: {
      headline: "Make your profile clearer, sharper, and more credible.",
      body: "Scott can help improve your social presence with stronger profile positioning, better audience framing, clearer proof, and content that supports trust over time.",
      primaryLabel: "Improve My Social Presence",
      secondaryLabel: "View Services",
    },
  },
  strong: {
    label: "Trust signals: strong",
    diagnosis:
      "Your profile is strong. It likely communicates who you are and what you do, but there is still room to sharpen the positioning, improve conversion, strengthen proof, and align it with your broader content strategy.",
    nextStep:
      "Tighten the headline, swap one generic line for a specific proof point, and test a sharper CTA.",
    cta: {
      headline: "You have a strong profile. Now make it work harder.",
      body: "Scott can help optimize your profile, improve your content system around it, strengthen SEO and discoverability, and turn your social presence into a more consistent trust-building asset.",
      primaryLabel: "Optimize My Social Presence",
      secondaryLabel: "View Services",
    },
  },
  excellent: {
    label: "Trust signals: excellent",
    diagnosis:
      "This is a strong profile with clear trust signals. The opportunity is not fixing the basics. It is systemizing the content around it, improving discoverability, testing stronger CTAs, and freeing you to focus on higher-value work.",
    nextStep:
      "Systemize the content around the profile so the trust it earns compounds across platforms.",
    cta: {
      headline: "Your profile is strong. Let's systemize the presence around it.",
      body: "Scott can help turn a strong profile into a stronger content engine with better platform strategy, SEO alignment, thought leadership, repurposing, and consistent audience growth.",
      primaryLabel: "Optimize and Systemize My Social Presence",
      secondaryLabel: "View Services",
    },
  },
};

interface ScanResult {
  score: number;
  trustGaps: string[];
  signals: string;
  quickFix: string;
}

function SocialProfileScanPage() {
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const [goal, setGoal] = useState(GOALS[0]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limitHit, setLimitHit] = useState(false);
  const [sessionDone, setSessionDone] = useState(false);
  const [mode, setMode] = useState<"url" | "paste">("url");
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchedFrom, setFetchedFrom] = useState<string | null>(null);

  useEffect(() => {
    // Disabled session check temporarily for easy testing
    /*
    if (typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY)) {
      setSessionDone(true);
    }
    */
    // Check remaining daily quota up front so the limit notice shows
    // before the user fills in a scan they can't run.
    let cancelled = false;
    fetch("/api/public/tool-quota")
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled && json && typeof json.remaining === "number" && json.remaining <= 0) {
          setLimitHit(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  async function checkQuotaBeforeReset() {
    try {
      const res = await fetch("/api/public/tool-quota");
      const json = res.ok ? await res.json() : null;
      if (json && typeof json.remaining === "number" && json.remaining <= 0) {
        setLimitHit(true);
        return false;
      }
    } catch {
      // fail open
    }
    return true;
  }

  const platformInfo = PLATFORM_LIMITS[platform] ?? { limit: null, field: "bio" };
  const platformLimit = platformInfo.limit;
  const overPlatformLimit = platformLimit != null && text.length > platformLimit;
  const remaining = 1500 - text.length;
  const canSubmit = text.trim().length >= 20 && text.length <= 1500 && !loading;

  async function handleFetchUrl() {
    setError(null);
    setFetchedFrom(null);
    if (!url.trim()) {
      setError("Paste a public profile URL.");
      return;
    }
    setFetching(true);
    try {
      const res = await fetch("/api/public/profile-fetch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.error ?? "Couldn't fetch that page.");
        return;
      }
      setText(String(json.text ?? "").slice(0, 1500));
      setFetchedFrom(json.sourceTitle ?? url.trim());
      setMode("paste");
    } catch {
      setError("Couldn't reach the fetcher. Try pasting your bio manually.");
    } finally {
      setFetching(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    const startedAt = Date.now();
    try {
      const res = await fetch("/api/public/profile-scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform:
            platformLimit != null
              ? `${platform} (hard limit: ${platformLimit} chars for ${platformInfo.field})`
              : platform,
          goal,
          text: text.trim(),
        }),
      });
      const elapsed = Date.now() - startedAt;
      if (elapsed < 2400) await new Promise((r) => setTimeout(r, 2400 - elapsed));
      if (res.status === 429) {
        const json = await res.json().catch(() => ({}));
        if (json?.limitHit) setLimitHit(true);
        else setError(json?.error ?? "Please slow down and try again in a moment.");
        setLoading(false);
        return;
      }
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setError(json?.error ?? "Something went wrong. Try again.");
        setLoading(false);
        return;
      }
      const data = (await res.json()) as ScanResult;
      setResult(data);
      // Disabled setting session storage so user can test multiple scans
      // if (typeof window !== "undefined") sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      setError("Couldn't reach the diagnostic. Try again.");
    } finally {
      setLoading(false);
    }
  }

  const band = result ? bandFor(result.score) : null;
  const levelCopy = band ? LEVEL_COPY[band] : null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <section className="relative isolate overflow-hidden bg-foreground text-cream">
        <DarkSectionBackdrop />
        <div className="relative mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <Link
            to="/tools"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-cream/70 hover:text-cream"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All tools
          </Link>
          <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-display text-4xl text-cream sm:text-5xl">
                Social Profile <span className="text-gradient">Trust</span> Scan
              </h1>
              <p className="mt-4 max-w-2xl text-lg text-cream/75">
                Check whether your social profiles, from LinkedIn and X to Bluesky and GitHub,
                build trust or just list what you do.
              </p>
            </div>
            <TrustSignalMeter />
          </div>
          <div className="mt-8 flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream/65">
            {[
              { Icon: Shield, label: "Trust signals" },
              { Icon: Target, label: "Positioning" },
              { Icon: PenLine, label: "Proof gaps" },
              { Icon: Sparkles, label: "Quick fix" },
            ].map(({ Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-cream/15 bg-cream/5 px-3 py-1 backdrop-blur-sm"
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="relative isolate flex-1 overflow-hidden">
        <LightSectionBackdrop variant="analytics" arcs={false} sparkles={false} />
        <div className="mx-auto max-w-3xl px-6 pt-12 pb-24 sm:pb-32">
          {limitHit ? (
            <LimitHitCard tool="social-profile-scan" />
          ) : sessionDone && !result ? (
            <AlreadyTakenCard />
          ) : result && levelCopy ? (
            <div className="space-y-6">
              <ResultCard
                tool="social-profile-scan"
                score={result.score}
                levelLabel={levelCopy.label}
                diagnosis={
                  result.signals
                    ? `${levelCopy.diagnosis}\n\nWhat this profile currently signals: ${result.signals}`
                    : levelCopy.diagnosis
                }
                gaps={result.trustGaps}
                nextStep={`One quick fix: ${result.quickFix}`}
                cta={levelCopy.cta}
              />
              <div className="flex justify-center pt-4">
                <button
                  onClick={async () => {
                    const allowed = await checkQuotaBeforeReset();
                    if (!allowed) return;
                    setResult(null);
                    setText("");
                    setUrl("");
                    setFetchedFrom(null);
                    setMode("url");
                  }}
                  className="rounded-full border border-ink/20 px-6 py-2.5 text-sm font-semibold text-ink transition hover:bg-ink/5 cursor-pointer"
                >
                  Scan another profile
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="relative group/form-card">
              {/* Moving Gradient Ambient Glow */}
              <div
                className={`pointer-events-none absolute -inset-4 -z-10 rounded-[36px] opacity-40 blur-2xl transition-all duration-700 ${
                  loading
                    ? "opacity-90 scale-102"
                    : "group-hover/form-card:opacity-75 group-hover/form-card:scale-101"
                }`}
                style={{
                  background:
                    "linear-gradient(135deg, color-mix(in oklab, #3b82f6 30%, transparent), color-mix(in oklab, #7c3aed 35%, transparent) 50%, color-mix(in oklab, #10b981 25%, transparent))",
                  backgroundSize: "200% 200%",
                  animation: loading
                    ? "gradientMove 3s ease infinite"
                    : "gradientMove 8s ease infinite",
                }}
              />
              {/* Glowing animated border outline */}
              <div
                className={`pointer-events-none absolute -inset-[1.5px] -z-10 rounded-[25px] transition-all duration-500 ${
                  loading ? "opacity-100" : "opacity-0 group-hover/form-card:opacity-100"
                }`}
                style={{
                  background: "linear-gradient(90deg, #3b82f6, #7c3aed, #10b981, #3b82f6)",
                  backgroundSize: "200% auto",
                  animation: "gradientMove 4s linear infinite",
                }}
              />
              <div
                className={`relative space-y-6 rounded-3xl border bg-card p-6 sm:p-8 transition-all duration-500 ${
                  loading
                    ? "border-transparent shadow-2xl"
                    : "border-border group-hover/form-card:border-transparent group-hover/form-card:shadow-lg"
                }`}
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                <Field label="Platform">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                    {PLATFORM_DEFS.map(({ name, Icon }) => {
                      const active = platform === name;
                      return (
                        <button
                          type="button"
                          key={name}
                          onClick={() => setPlatform(name)}
                          className={`group flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-medium transition-all duration-200 ${
                            active
                              ? "border-ink bg-ink text-cream shadow-md"
                              : "border-border bg-background text-ink hover:-translate-y-0.5 hover:border-ink/40 hover:shadow-sm"
                          }`}
                        >
                          <Icon
                            className={`h-4 w-4 shrink-0 transition-transform duration-200 ${active ? "" : "group-hover:scale-110"}`}
                          />
                          <span className="truncate">{name}</span>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                <Field label="Goal">
                  <div className="relative">
                    <Target className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <select
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                      className="w-full cursor-pointer appearance-none rounded-xl border border-border bg-background py-2.5 pl-10 pr-4 text-sm text-ink transition-all hover:border-ink/40 focus:border-ink/60 focus:outline-none focus:ring-2 focus:ring-ink/10"
                    >
                      {GOALS.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>

                <div className="flex rounded-full border border-border bg-background p-1 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setMode("url")}
                    className={`flex-1 rounded-full px-4 py-2 transition-all duration-200 ${mode === "url" ? "bg-ink text-cream shadow-sm" : "text-muted-foreground hover:text-ink"}`}
                  >
                    <Link2 className="mr-1.5 inline h-3.5 w-3.5" /> Fetch from URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("paste")}
                    className={`flex-1 rounded-full px-4 py-2 transition-all duration-200 ${mode === "paste" ? "bg-ink text-cream shadow-sm" : "text-muted-foreground hover:text-ink"}`}
                  >
                    <PenLine className="mr-1.5 inline h-3.5 w-3.5" /> Paste manually
                  </button>
                </div>

                {mode === "url" ? (
                  <Field label="Public profile URL">
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <div className="group relative flex-1">
                        <Globe className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-ink" />
                        <input
                          type="url"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          placeholder="https://x.com/@username"
                          className="w-full rounded-xl border border-border bg-background py-2.5 pl-10 pr-4 text-sm text-ink placeholder:text-muted-foreground transition-all hover:border-ink/40 focus:border-ink/60 focus:outline-none focus:ring-2 focus:ring-ink/10"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleFetchUrl}
                        disabled={fetching || !url.trim()}
                        className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream transition-all duration-200 hover:opacity-90 hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {fetching ? "Fetching…" : "Fetch bio"}
                      </button>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      <span className="text-ink">Works:</span> Bluesky, Mastodon, GitHub, YouTube
                      channel <span className="font-mono">/about</span>, Substack/Medium author
                      pages.
                      <br />
                      <span className="text-ink">Doesn't work</span> (login walls or anti-bot):
                      X, LinkedIn, Instagram, Threads, Facebook, TikTok - paste those manually.
                    </p>
                  </Field>
                ) : (
                  <Field label="Profile text">
                    {fetchedFrom && (
                      <p className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs text-emerald-800">
                        <Sparkles className="h-3 w-3" />
                        Pulled from <span className="text-ink">{fetchedFrom}</span>. Edit before
                        scanning if needed.
                      </p>
                    )}
                    {platformLimit != null && (
                      <p className="mb-1.5 text-xs text-muted-foreground">
                        <span className="text-ink">{platform}</span> {platformInfo.field} limit:{" "}
                        <span className="font-mono">{platformLimit}</span> characters. The
                        diagnostic will respect this limit.
                      </p>
                    )}
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value.slice(0, 1500))}
                      rows={8}
                      placeholder="Paste your social bio, headline, or profile intro. Limit 1,500 characters."
                      maxLength={1500}
                      className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm leading-relaxed text-ink placeholder:text-muted-foreground transition-all hover:border-ink/40 focus:border-ink/60 focus:outline-none focus:ring-2 focus:ring-ink/10"
                    />
                    <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                      <span>Minimum 20 characters.</span>
                      <span className={overPlatformLimit ? "text-primary" : ""}>
                        {platformLimit != null
                          ? `${text.length} / ${platformLimit}${overPlatformLimit ? " - over platform limit" : ""}`
                          : `${remaining} characters left`}
                      </span>
                    </div>
                  </Field>
                )}

                {error && (
                  <p className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit || mode === "url"}
                  className="group relative w-full overflow-hidden rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-cream transition-all duration-200 hover:opacity-95 hover:shadow-lg active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="relative z-10 inline-flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Activity className="h-4 w-4 animate-pulse" />
                        Scanning profile trust signals…
                      </>
                    ) : mode === "url" ? (
                      "Fetch a bio first"
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                        Scan Profile
                      </>
                    )}
                  </span>
                  <span
                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-cream/15 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                    aria-hidden="true"
                  />
                </button>

                <p className="text-xs italic text-muted-foreground">
                  This is a quick diagnostic, not a full strategy or audit. For a deeper review,
                  contact Scott.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function TrustSignalMeter() {
  return (
    <div
      className="flex items-center gap-3 rounded-2xl border border-cream/15 bg-cream/[0.04] px-4 py-3 backdrop-blur-sm"
      aria-label="Trust signal meter"
    >
      <div className="flex h-10 items-end gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="w-1.5 rounded-full"
            style={{
              height: `${30 + i * 15}%`,
              background: "linear-gradient(180deg, #7c3aed 0%, #3b82f6 60%, #10b981 100%)",
              animation: `tsmPulse 1.6s ease-in-out ${i * 0.18}s infinite`,
              transformOrigin: "bottom",
            }}
          />
        ))}
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cream/70">
        Trust
        <br />
        signal
      </div>
      <style>{`
        @keyframes tsmPulse { 
          0%, 100% { transform: scaleY(0.55); opacity: 0.55; } 
          50% { transform: scaleY(1); opacity: 1; } 
        }
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}

function AlreadyTakenCard() {
  return (
    <div
      className="rounded-2xl border border-border bg-card p-8 text-center"
      style={{ boxShadow: "var(--shadow-soft)" }}
    >
      <h2 className="font-display text-2xl text-ink">
        You've already run this scan in this session.
      </h2>
      <p className="mt-3 text-muted-foreground">
        One submission per tool per session. Come back tomorrow, or get a deeper review now.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/contact"
          className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
        >
          Contact Scott
        </Link>
        <Link
          to="/services"
          className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink transition hover:bg-ink/5"
        >
          View Services
        </Link>
      </div>
    </div>
  );
}
